import {
  Avatar,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Step,
  StepConnector,
  StepIconProps,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  makeStyles,
  withStyles
} from '@material-ui/core/styles';
import {
  CheckBoxOutlineBlankRounded,
  CheckBoxRounded,
  ClearRounded,
  CreateNewFolderRounded,
  DeleteRounded,
  FolderOutlined,
  Looks3Rounded,
  LooksOneRounded,
  LooksTwoRounded,
  PeopleAltOutlined,
  TimerOutlined
} from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';
import clsx from 'clsx';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../app/store';
import HelpTooltip from '../../components/HelpTooltip';
import HierarchySelect from '../../components/HierarchySelect';
import { stringAvatar } from '../../functions/stringAvatar';
import { useAppDispatch } from '../../hooks/useAppRudux';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import { AccessControl } from '../MasterData/AccessControl/accessControlSlice';
import { ApprovalPeriod } from '../ProjectApprovalPeriod/projectApprovalPeriodSlice';
import { User } from '../UserProfile/userProfileSlice';
import {
  ProjectRegister,
  getProjectRegistersAsync,
  getUsersInProjectRegisterAsync
} from './projectRegisterSlice';
import { createProjectAsync } from './projectSlice';

enum StepperLabel {
  PROJECT = 0,
  MEMBER_ACCESS_CONTROL = 1,
  APPROVAL_PERIODS = 2,
  COMPLETED = 3
}

interface StepWorkflow {
  label: string;
  icon: JSX.Element;
}

export default function ProjectCreate() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { data: accessControls } = useSelector(
    (state: RootState) => state.accessControls
  );
  const { projects, users } = useSelector(
    (state: RootState) => state.projectRegisters
  );
  const { data: workflowSteps } = useSelector(
    (state: RootState) => state.workflowSteps
  );

  const steps: StepWorkflow[] = [
    { label: t('projectCreate.selectProject'), icon: <FolderOutlined fontSize="small" /> },
    {
      label: t('projectCreate.assignMembers'),
      icon: <PeopleAltOutlined fontSize="small" />
    },
    { label: t('projectCreate.setApprovalPeriods'), icon: <TimerOutlined fontSize="small" /> }
  ];
  const [activeStep, setActiveStep] = useState<number>(0);

  const [selectedProject, setSelectedProject] =
    useState<ProjectRegister | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<User[]>([]);
  const [selectedAccessControls, setSelectedAccessControls] = useState<
    AccessControl[]
  >([]);
  const [memberLists, setMemberLists] = useState<
    { user: User; accessControls: AccessControl[] }[]
  >([]);
  const defaultApprovalPeriod = workflowSteps.map((workflowStep) => {
    return { workflowStepId: workflowStep.id, value: null };
  });
  const [approvalPeriods, setApprovalPeriods] = useState<ApprovalPeriod[]>(
    defaultApprovalPeriod
  );
  const [loading, setLoading] = useState<boolean>(false);

  const showAlertRequestAccess = users.some(
    ({ roles }) => roles && roles.length === 0
  );

  useEffect(() => {
    dispatch(getProjectRegistersAsync());
  }, []);

  function onChangeAccessControl(accessControls: AccessControl[]): void {
    setSelectedAccessControls(accessControls);
  }

  function onChangeAccessControlByUserId(
    userId: number
  ): (accessControls: AccessControl[]) => void {
    return function (accessControls) {
      const memberListIndex = memberLists.findIndex(
        (memberList) => memberList.user.id === userId
      );
      const newMemberList = {
        ...memberLists[memberListIndex],
        accessControls
      };
      const newMemberLists = [
        ...memberLists.slice(0, memberListIndex),
        newMemberList,
        ...memberLists.slice(memberListIndex + 1)
      ];
      setMemberLists(newMemberLists);
    };
  }

  function onChangeApprovalPeriods(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    const { name, value } = event.target;
    const workflowStepId = Number.parseInt(name, 10);
    const newValue = Number.parseInt(value, 10);
    const newApprovalPeriods = approvalPeriods.filter(
      (approvalPeriod) => approvalPeriod.workflowStepId !== workflowStepId
    );
    newApprovalPeriods.push({
      workflowStepId,
      value: value === '' || newValue <= 0 ? null : newValue
    });
    setApprovalPeriods(newApprovalPeriods);
  }

  async function onSubmit(
    event: React.FormEvent<HTMLFormElement>,
    selectedProject: ProjectRegister,
    memberLists: { user: User; accessControls: AccessControl[] }[],
    approvalPeriods: ApprovalPeriod[]
  ): Promise<void> {
    event.preventDefault();
    try {
      setLoading(true);
      await dispatch(
        createProjectAsync({
          projectRegisterId: selectedProject.id,
          projectAccessControlUsers: memberLists.map((member) => {
            return {
              userId: member.user.id,
              accessControlIds: member.accessControls.map(
                (accessControl) => accessControl.id
              )
            };
          }),
          approvalPeriods: approvalPeriods.filter(
            (approvalPeriod) =>
              approvalPeriod.value !== null && approvalPeriod.value !== 0
          )
        })
      ).unwrap();
      dispatch(
        loadingSlice.actions.success({
          message: t('messages.success.projectCreated') as string
        })
      );
      setActiveStep(StepperLabel.COMPLETED);
      setTimeout(() => {
        history.push('/projects');
      }, 1000);
    } catch (error) {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb name="setUpNewProject" />
      <Typography className={classes.title} component="p" variant="h4">
        {t('projectCreate.createProject')}
      </Typography>
      <Paper className={classes.paper}>
        <Stepper
          className={classes.stepper}
          alternativeLabel
          activeStep={activeStep}
          connector={<Connector />}
        >
          {steps.map(({ label, icon }) => (
            <Step key={label}>
              <StepLabel StepIconComponent={(props) => StepIcon(props, icon)}>
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>
      <form
        onSubmit={(event) => {
          if (selectedProject)
            onSubmit(event, selectedProject, memberLists, approvalPeriods);
        }}
      >
        <Paper className={classes.paper}>
          <div className={classes.stepText}>
            <LooksOneRounded color="primary" />
            <Typography variant="subtitle2">{t('projectCreate.selectProject')}</Typography>
          </div>
          <Autocomplete
            className={classes.fullWidth}
            id="project"
            options={projects}
            value={selectedProject}
            getOptionLabel={(option) =>
              `[ ${option?.projectNo} ] ${option?.name}`
            }
            onChange={(event, newValue) => {
              setSelectedProject(newValue);
              setSelectedMembers([]);
              setSelectedAccessControls([]);
              setMemberLists([]);
              if (newValue && newValue.id !== selectedProject?.id) {
                dispatch(getUsersInProjectRegisterAsync(newValue.id));
              }
              newValue
                ? setActiveStep(StepperLabel.MEMBER_ACCESS_CONTROL)
                : setActiveStep(StepperLabel.PROJECT);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder={t('projectCreate.selectProjectPlaceholder') as string}
                required
              />
            )}
          />
        </Paper>
        <Collapse in={Boolean(selectedProject)}>
          <Paper className={classes.paper}>
            <div className={classes.stepText}>
              <LooksTwoRounded color="primary" />
              <Typography variant="subtitle2">
                {t('projectCreate.assignMembers')}
              </Typography>
            </div>
            {showAlertRequestAccess && (
              <Alert
                className={classes.alert}
                severity="info"
                icon={false}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      window.open(
                        `${process.env.REACT_APP_REQUEST_FOR_BACKSPACE_WEB_URL}?systemName=doc`,
                        '_blank'
                      );
                    }}
                  >
                    {t('projectCreate.requestAccess')}
                  </Button>
                }
              >
                <span dangerouslySetInnerHTML={{ __html: t('projectCreate.assignAccessInfo') as string }} />
              </Alert>
            )}
            <Grid container spacing={1}>
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  id="add-member"
                  size="small"
                  multiple
                  disableCloseOnSelect
                  options={users.filter(
                    (user) =>
                      !memberLists.some(
                        (memberList) => memberList.user.id === user.id
                      )
                  )}
                  getOptionLabel={(option) =>
                    `${option?.firstName} ${option?.lastName}`
                  }
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        label={`${option?.firstName} ${option?.lastName}`}
                        {...getTagProps({ index })}
                        variant="outlined"
                        size="small"
                        deleteIcon={<ClearRounded />}
                      />
                    ))
                  }
                  value={selectedMembers}
                  getOptionSelected={(option, value) => option.id === value?.id}
                  getOptionDisabled={(option) => option.roles?.length === 0}
                  onChange={(event, newValue) => {
                    setSelectedMembers(newValue);
                    if (newValue.length === 0) {
                      setSelectedAccessControls([]);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={
                        selectedMembers.length === 0 ? t('projectCreate.selectMemberPlaceholder') as string : ''
                      }
                    />
                  )}
                  disabled={!selectedProject}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <HierarchySelect<AccessControl>
                  placeholder={t('projectCreate.accessLevelPlaceholder') as string}
                  options={accessControls}
                  value={selectedAccessControls}
                  onChange={onChangeAccessControl}
                  required={selectedMembers.length > 0}
                />
              </Grid>
            </Grid>
            <Collapse in={selectedMembers.length > 0}>
              <div className={classes.collapseButton}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={selectedAccessControls.length === 0}
                  onClick={() => {
                    const members = selectedMembers.map((member) => {
                      return {
                        user: member,
                        accessControls: selectedAccessControls
                      };
                    });
                    const newMemberLists = memberLists.filter(
                      (memberList) =>
                        !members.some(
                          (member) => member.user.id === memberList.user.id
                        )
                    );
                    setMemberLists([...newMemberLists, ...members]);
                    setSelectedMembers([]);
                    setSelectedAccessControls([]);
                    setActiveStep(StepperLabel.APPROVAL_PERIODS);
                  }}
                >
                  {t('projectCreate.add')}
                </Button>
                <Button
                  className={classes.marginLeft}
                  fullWidth
                  onClick={() => {
                    setSelectedMembers([]);
                    setSelectedAccessControls([]);
                  }}
                >
                  {t('projectCreate.cancel')}
                </Button>
              </div>
            </Collapse>
            <Grid
              className={clsx(classes.marginTop, {
                [classes.clearMarginTop]: memberLists.length === 0
              })}
              container
              spacing={memberLists.length === 0 ? 0 : 1}
            >
              {memberLists
                .sort((a, b) => {
                  const nameA = `${a.user.firstName.toUpperCase()}${a.user.lastName.toUpperCase()}`; // ignore upper and lowercase
                  const nameB = `${b.user.firstName.toUpperCase()}${b.user.lastName.toUpperCase()}`; // ignore upper and lowercase
                  return nameA.localeCompare(nameB);
                })
                .map((memberList) => {
                  return (
                    <Fragment key={memberList.user.id}>
                      <Grid
                        className={classes.flexAlignCenter}
                        item
                        xs={12}
                        sm={4}
                      >
                        <Avatar
                          className={classes.avatar}
                          {...stringAvatar(
                            `${memberList.user.firstName} ${memberList.user.lastName}`
                          )}
                        />
                        <Typography
                          className={classes.lineHeight120}
                        >{`${memberList.user.firstName} ${memberList.user.lastName}`}</Typography>
                      </Grid>
                      <Grid
                        className={classes.flexAlignCenter}
                        item
                        xs={12}
                        sm={8}
                      >
                        <HierarchySelect<AccessControl>
                          placeholder={t('projectCreate.accessLevelPlaceholder') as string}
                          options={accessControls}
                          value={memberList.accessControls}
                          onChange={onChangeAccessControlByUserId(
                            memberList.user.id
                          )}
                          required
                        />
                        <Tooltip title={t('projectCreate.remove') as string} arrow>
                          <IconButton
                            className={classes.marginLeft}
                            size="small"
                            onClick={() => {
                              const newMemberLists = memberLists.filter(
                                (member) =>
                                  memberList.user.id !== member.user.id
                              );
                              setMemberLists(newMemberLists);
                              if (newMemberLists.length === 0)
                                setActiveStep(
                                  StepperLabel.MEMBER_ACCESS_CONTROL
                                );
                            }}
                          >
                            <DeleteRounded />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Fragment>
                  );
                })}
            </Grid>
          </Paper>
        </Collapse>
        <Collapse in={memberLists.length > 0}>
          <Paper className={classes.paper}>
            <div className={classes.stepText}>
              <Looks3Rounded color="primary" />
              <Typography variant="subtitle2">{t('projectCreate.setApprovalPeriods')}</Typography>
              <HelpTooltip>
                {t('projectCreate.helpApprovalPeriods')}
              </HelpTooltip>
            </div>
            <Collapse in={selectedMembers.length > 0}>
              <Alert className={classes.marginBottom} severity="warning">
                {t('projectCreate.dontForgetMembers')}
              </Alert>
            </Collapse>
            <Grid container spacing={0} justify="flex-start">
              <Grid item xs={12} md={6}>
                {workflowSteps.map((workflowStep) => {
                  const approvalPeriod = approvalPeriods.find(
                    (approvalPeriod) =>
                      approvalPeriod.workflowStepId === workflowStep.id
                  );
                  return (
                    <div
                      className={clsx(
                        classes.marginBottom,
                        classes.positionText
                      )}
                      key={workflowStep.name}
                    >
                      <Typography className={classes.flexGrow}>
                        {workflowStep.name}
                      </Typography>
                      <TextField
                        className={classes.numberField}
                        name={`${workflowStep.id}`}
                        type="number"
                        variant="outlined"
                        size="small"
                        value={
                          approvalPeriod?.value ? approvalPeriod.value : ''
                        }
                        onChange={onChangeApprovalPeriods}
                      />
                      <Typography>{t('projectCreate.days')}</Typography>
                    </div>
                  );
                })}
              </Grid>
            </Grid>
          </Paper>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={
              loading ||
              approvalPeriods.every(
                (approvalPeriod) => approvalPeriod.value === null
              )
            }
            startIcon={<CreateNewFolderRounded />}
          >
            {t('projectCreate.addThisProject')}
          </Button>
        </Collapse>
      </form>
    </div>
  );
}

function StepIcon(props: StepIconProps, icon: JSX.Element) {
  const classes = useStepIconStyles();
  const { active, completed } = props;

  return (
    <div
      className={clsx(classes.root, {
        [classes.active]: active,
        [classes.success]: completed
      })}
    >
      {icon}
    </div>
  );
}

const Connector = withStyles((theme: Theme) =>
  createStyles({
    alternativeLabel: {
      top: 16
    },
    active: {
      '& $line': {
        backgroundImage: `linear-gradient( 90deg,${theme.palette.success.main} 0%,${theme.palette.primary.main} 100%)`
      }
    },
    completed: {
      '& $line': {
        backgroundColor: theme.palette.success.main
      }
    },
    line: {
      height: 2,
      border: 0,
      backgroundColor: theme.palette.divider,
      borderRadius: 1
    }
  })
)(StepConnector);

const useStepIconStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: theme.palette.divider,
      zIndex: 1,
      color: '#fff',
      width: 32,
      height: 32,
      display: 'flex',
      borderRadius: '50%',
      justifyContent: 'center',
      alignItems: 'center'
    },
    active: {
      backgroundColor: theme.palette.primary.main,
      boxShadow: '0 4px 12px 0 rgba(0, 0, 0, .25)'
    },
    success: {
      backgroundColor: theme.palette.success.main
    }
  })
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      borderRadius: 16,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2)
    },
    stepper: {
      padding: theme.spacing(1, 0, 0, 0)
    },
    stepText: {
      display: 'flex',
      alignItems: 'center',
      margin: theme.spacing(0.5, 0, 1.5, 0),
      '& > :nth-child(1)': {
        marginRight: theme.spacing(1)
      }
    },
    positionText: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 2)
    },
    numberField: {
      width: 80,
      paddingRight: 16
    },
    alert: {
      marginBottom: theme.spacing(1)
    },
    marginLeft: {
      marginLeft: theme.spacing(1)
    },
    marginRight: {
      marginRight: theme.spacing(1)
    },
    marginTop: {
      marginTop: theme.spacing(2)
    },
    marginBottom: {
      marginBottom: theme.spacing(2),
      '&:last-child': {
        marginBottom: 0
      }
    },
    clearMarginTop: {
      marginTop: 0
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexGrow: {
      flexGrow: 1
    },
    fullWidth: {
      width: '100%'
    },
    checkIcon: {
      margin: theme.spacing(-2)
    },
    collapseButton: {
      display: 'flex',
      paddingTop: theme.spacing(1)
    },
    avatar: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      marginRight: theme.spacing(1)
    },
    lineHeight120: {
      lineHeight: '120%'
    }
  })
);
